import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';

import './Floorplans.css';

import { GetDataPage, RegisterDataPage, UnRegisterDataPage } from '../../interface/Data';
import { SetStatusGlobal } from '../../interface/Global';
import { SetModalData, SetModalState } from '../../interface/PopUp';

import Comp_Select from '../../components/Select';
import type { SelectItem, SelectOption } from '../../components/Select';

import { AnalyticsFloorplan, GetDataFloorplans, IsNotSet } from './Analytics';
import { ProjectNamesByCompany, ReportClientMap, ReportProjectMap, ShowClientList, SortOptions } from './Filters';

// os dados do Analytics chegam achatados e são agrupados em country > client >
// project > floorplans
interface ProjectGroup {
    project : string;
    region  : string;
    city    : string;
    list    : AnalyticsFloorplan[];
}

interface ClientGroup {
    client   : string;
    projects : ProjectGroup[];
}

interface CountryGroup {
    country : string;
    clients : ClientGroup[];
}

interface BedroomChart {
    city     : string;
    count    : number;
    bedrooms : string;
    fill     : string;
}

const BEDROOM_COLORS: { [key: string]: string } = {
    '1': '#6366f1', '2': '#f28e2b', '3': '#e15759',
    '4': '#76b7b2', '5': '#59a14f', '6': '#edc948',
};

export default function Page_Reports_Floorplans() {

    const [ listClients, setListClients ] = useState<ReportClientMap>(GetDataPage('clients') || {});
    const [ listProjects, setListProjects ] = useState<ReportProjectMap>(GetDataPage('projects') || {});

    const [ company, setCompany ] = useState<SelectOption<number>>({ "value": 0, "label": "All clients", "qtd_project": 0 });
    const [ country, setCountry ] = useState<SelectOption<string>>({ "value": "", "label": "All countries" });
    const [ region, setRegion ] = useState<SelectOption<string>>({ "value": "", "label": "All regions" });
    const [ city, setCity ] = useState<SelectOption<string>>({ "value": "", "label": "All cities" });
    const [ project, setProject ] = useState<SelectOption<string>>({ "value": "", "label": "All projects" });

    const [ dataFloorplans, setDataFloorplans ] = useState<CountryGroup[]>([]);

    function OpenGraphData(data: { city: string; project: { client: string; project: string; list: AnalyticsFloorplan[] }[] }){
        SetModalData('GraphData', { "data": data });
        SetModalState('GraphData', true);
    }

    function ShowDataAnalytics(){
        SetStatusGlobal('loading', true);

        GetDataFloorplans()
        .then(response => {
            const flat = (response.floorplans ?? []).filter(item =>
                !IsNotSet(item.country) && !IsNotSet(item.client) && !IsNotSet(item.floorplan_title)
            );

            const countryMap: { [country: string]: { country: string; clients: { [client: string]: { client: string; projects: { [project: string]: ProjectGroup } } } } } = {};
            for (const item of flat) {
                const countryKey = item.country;
                const clientKey  = item.client;
                const projectKey = item.project + '|' + item.region + '|' + item.city;

                if (!countryMap[countryKey]) {
                    countryMap[countryKey] = { country: countryKey, clients: {} };
                }

                if (!countryMap[countryKey].clients[clientKey]) {
                    countryMap[countryKey].clients[clientKey] = { client: clientKey, projects: {} };
                }

                if (!countryMap[countryKey].clients[clientKey].projects[projectKey]) {
                    countryMap[countryKey].clients[clientKey].projects[projectKey] = {
                        project: item.project,
                        region:  item.region,
                        city:    item.city,
                        list: [],
                    };
                }

                countryMap[countryKey].clients[clientKey].projects[projectKey].list.push(item);
            }

            const grouped: CountryGroup[] = Object.values(countryMap).map(c => ({
                country: c.country,
                clients: Object.values(c.clients).map(cl => ({
                    client:   cl.client,
                    projects: Object.values(cl.projects),
                })),
            }));

            setDataFloorplans(grouped);
            SetStatusGlobal('loading', false);
        })
        .catch(error => {
            SetStatusGlobal('loading', false);
            console.error("Erro ao carregar floorplans:", error);
        });
    }

    function getDataFilteredByCompany(): CountryGroup[] {
        let result = dataFloorplans;
        if (company.value) {
            const projectNamesForCompany = ProjectNamesByCompany(listProjects, company.value);
            if (projectNamesForCompany.length === 0) return [];
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cl => ({
                            ...cl,
                            projects: cl.projects.filter(p => projectNamesForCompany.includes(p.project.trim().toLowerCase()))
                        }))
                        .filter(cl => cl.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }
        return result;
    }

    function getAllProjects(data: CountryGroup[]): ProjectGroup[] {
        return data.flatMap(c => c.clients.flatMap(cl => cl.projects));
    }

    function ShowCountryList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All countries" }];
        getDataFilteredByCompany().forEach(item => {
            if (!newData.find(c => c.id === item.country)) {
                newData.push({ "id": item.country, "name": item.country });
            }
        });
        return SortOptions(newData);
    }

    function ShowRegionList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All regions" }];
        let base = getDataFilteredByCompany();
        if (country.value) base = base.filter(c => c.country === country.value);
        getAllProjects(base).forEach(p => {
            if (p.region && !newData.find(r => r.id === p.region)) {
                newData.push({ "id": p.region, "name": p.region });
            }
        });
        return SortOptions(newData);
    }

    function ShowCityList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All cities" }];
        let base = getDataFilteredByCompany();
        if (country.value) base = base.filter(c => c.country === country.value);
        let projects = getAllProjects(base);
        if (region.value) projects = projects.filter(p => p.region === region.value);
        projects.forEach(p => {
            if (p.city && !newData.find(ci => ci.id === p.city)) {
                newData.push({ "id": p.city, "name": p.city });
            }
        });
        return SortOptions(newData);
    }

    function ShowProjectList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All projects" }];
        let base = getDataFilteredByCompany();
        if (country.value) base = base.filter(c => c.country === country.value);
        let projects = getAllProjects(base);
        if (region.value) projects = projects.filter(p => p.region === region.value);
        if (city.value) projects = projects.filter(p => p.city === city.value);
        projects.forEach(p => {
            if (!newData.find(item => item.id === p.project)) {
                newData.push({ "id": p.project, "name": p.project });
            }
        });
        return SortOptions(newData);
    }

    function handleSetCompany(val: SelectOption<number>){
        setCompany(val);
        setCountry({ "value": "", "label": "All countries" });
        setRegion({ "value": "", "label": "All regions" });
        setCity({ "value": "", "label": "All cities" });
        setProject({ "value": "", "label": "All projects" });
    }

    function handleSetCountry(val: SelectOption<string>){
        setCountry(val);
        setRegion({ "value": "", "label": "All regions" });
        setCity({ "value": "", "label": "All cities" });
        setProject({ "value": "", "label": "All projects" });
    }

    function handleSetRegion(val: SelectOption<string>){
        setRegion(val);
        setCity({ "value": "", "label": "All cities" });
        setProject({ "value": "", "label": "All projects" });
    }

    function handleSetCity(val: SelectOption<string>){
        setCity(val);
        setProject({ "value": "", "label": "All projects" });
    }

    function FilteredData(): CountryGroup[] {
        let result = getDataFilteredByCompany();

        if (country.value) {
            result = result.filter(c => c.country === country.value);
        }

        if (region.value) {
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cl => ({ ...cl, projects: cl.projects.filter(p => p.region === region.value) }))
                        .filter(cl => cl.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }

        if (city.value) {
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cl => ({ ...cl, projects: cl.projects.filter(p => p.city === city.value) }))
                        .filter(cl => cl.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }

        if (project.value) {
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cl => ({ ...cl, projects: cl.projects.filter(p => p.project === project.value) }))
                        .filter(cl => cl.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }

        return result
            .sort((a, b) => a.country.localeCompare(b.country))
            .map(c => ({
                ...c,
                clients: c.clients
                    .sort((a, b) => a.client.localeCompare(b.client))
                    .map(cl => ({
                        ...cl,
                        projects: cl.projects
                            .sort((a, b) => a.project.localeCompare(b.project))
                            .map(proj => ({
                                ...proj,
                                list: proj.list.sort((a, b) => a.floorplan_title.localeCompare(b.floorplan_title)),
                            })),
                    })),
            }));
    }

    function BuildBedroomChartData(): BedroomChart[] {
        const cityBedrooms: { [city: string]: { [bedrooms: string]: number } } = {};

        const isNotSetChart = (value: string) => !value || ['(not set)', 'not set', '(não definido)', 'não definido', 'n/a'].includes(String(value).trim().toLowerCase());

        filteredData.forEach(countryGroup => {
            countryGroup.clients.forEach(clientGroup => {
                clientGroup.projects.forEach(proj => {
                    const city = proj.city || proj.region || proj.project;
                    if (isNotSetChart(city)) return;
                    if (!cityBedrooms[city]) cityBedrooms[city] = {};
                    proj.list.forEach(item => {
                        const bed = String(item.floorplan_bedrooms ?? '');
                        if (isNotSetChart(bed)) return;
                        const count = parseInt(String(item.event_count)) || 0;
                        cityBedrooms[city][bed] = (cityBedrooms[city][bed] || 0) + count;
                    });
                });
            });
        });

        return Object.entries(cityBedrooms)
            .filter(([, bedroomsMap]) => Object.keys(bedroomsMap).length > 0)
            .map(([city, bedroomsMap]) => {
                const [topBed, topCount] = Object.entries(bedroomsMap)
                    .reduce((best, curr) => curr[1] > best[1] ? curr : best);
                return {
                    city,
                    count: topCount,
                    bedrooms: topBed,
                    fill: BEDROOM_COLORS[topBed] || '#8884d8',
                };
            })
            .sort((a, b) => b.count - a.count);
    }

    useEffect(() => {
        ShowDataAnalytics();

        RegisterDataPage('clients', setListClients);
        RegisterDataPage('projects', setListProjects);
        return ()=>{
            UnRegisterDataPage('clients', setListClients);
            UnRegisterDataPage('projects', setListProjects);
        };
    }, []);

    const filteredData = FilteredData();

    const chartData = BuildBedroomChartData();
    const uniqueBedrooms = Array.from(new Set(chartData.map(d => d.bedrooms))).sort();

    return(
        <div className="page_report page_floorplans">
            <div className="div_list_client margin_bottom">
                <Comp_Select title="Client" list={ ShowClientList(listClients) } value={ company } setValue={ handleSetCompany } required={ true } />

                <Comp_Select title="Project" list={ ShowProjectList() } value={ project } setValue={ setProject } required={ false } />
            </div>

            <div className="div_list_client">
                <Comp_Select title="Country" list={ ShowCountryList() } value={ country } setValue={ handleSetCountry } required={ false } />

                <Comp_Select title="Region" list={ ShowRegionList() } value={ region } setValue={ handleSetRegion } required={ false } />

                <Comp_Select title="City" list={ ShowCityList() } value={ city } setValue={ handleSetCity } required={ false } />
            </div>

            <div className="div_chart_bedrooms">
                {
                    chartData.length > 0 ?
                        <>
                            <div className="chart_title">Top bedroom type accessed per city</div>
                            <div className="chart_legend">
                                {
                                    uniqueBedrooms.map(bed => (
                                        <div key={ bed } className="legend_item">
                                            <span className="legend_dot" style={{ background: BEDROOM_COLORS[bed] || '#8884d8' }} />
                                            <span>{ bed }</span>
                                        </div>
                                    ))
                                }
                            </div>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={ chartData } margin={{ top: 24, right: 20, left: 0, bottom: 55 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                    <XAxis
                                        dataKey="city"
                                        tick={{ fill: '#aaa', fontSize: 12 }}
                                        angle={-35}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    <YAxis tick={{ fill: '#aaa', fontSize: 12 }} />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                        contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 6 }}
                                        labelStyle={{ color: '#fff' }}
                                        itemStyle={{ color: '#fff' }}
                                        formatter={ (value: any, _name: any, item: any) => [value, `${item.payload.bedrooms} bedroom${item.payload.bedrooms !== '1' ? 's' : ''}`] }
                                        labelFormatter={ (label: any) => `City: ${label}` }
                                    />
                                    <Bar
                                        dataKey="count"
                                        radius={[4, 4, 0, 0]}
                                        style={{ cursor: 'pointer' }}
                                        onClick={ (barData: any)=>{
                                            const city = barData.payload.city;
                                            const projectMap: { [key: string]: { client: string; project: string; list: AnalyticsFloorplan[] } } = {};
                                            filteredData.forEach(countryGroup => {
                                                countryGroup.clients.forEach(clientGroup => {
                                                    clientGroup.projects.forEach(proj => {
                                                        const projCity = proj.city || proj.region || proj.project;
                                                        if (projCity === city) {
                                                            const key = clientGroup.client + '|' + proj.project;
                                                            if (!projectMap[key]) {
                                                                projectMap[key] = {
                                                                    client: clientGroup.client,
                                                                    project: proj.project,
                                                                    list: [],
                                                                };
                                                            }
                                                            projectMap[key].list.push(...proj.list);
                                                        }
                                                    });
                                                });
                                            });
                                            OpenGraphData({ city, project: Object.values(projectMap) });
                                        } }
                                    >
                                        <LabelList dataKey="count" position="top" fill="#ffffff" style={{ fontSize: 11 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    : null
                }
            </div>
            <div className="list_data">
                {
                    filteredData.length === 0 && (company.value || country.value || region.value || city.value || project.value) ?
                        <div className="no_data_found">No data found for the selected filters</div>
                    : null
                }
                {
                    filteredData.map((country, country_index) =>{
                        return(
                            <div className="list_country" key={ "loop_" + country_index }>
                                <div className="name_country">{ country.country }</div>
                                <div className="list_client">
                                    {
                                        country.clients.map((clientGroup, client_index) =>(
                                            <div className="data_client" key={ "client_" + client_index }>
                                                <div className="client_name">
                                                    { clientGroup.client }
                                                </div>
                                                <div className="list_units">
                                                    {
                                                        clientGroup.projects.map((projectGroup, project_index) =>{
                                                            return(
                                                                <div className="div_project" key={ "project_" + project_index }>
                                                                    <div className="data_project">
                                                                        Project: <span className="data_project_name">{ projectGroup.project }</span>
                                                                    </div>
                                                                    {/*
                                                                    <div className="data_project inf_project">
                                                                        <div className="">
                                                                            Region: { projectGroup.region }
                                                                        </div>
                                                                        <div className="">
                                                                            City: { projectGroup.city }
                                                                        </div>
                                                                    </div>
                                                                    */}
                                                                    <div className="list_apart">
                                                                        <div className="inf_access div_topic">
                                                                            <div className="topic_unit">Floorplan</div>
                                                                            <div className="topic_number">Bathrooms</div>
                                                                            <div className="topic_number">Bedrooms</div>
                                                                            <div className="topic_number">Qtd Access</div>
                                                                        </div>
                                                                        {
                                                                            projectGroup.list.map((item, item_index) =>{
                                                                                return(
                                                                                    <div className="inf_access" key={ "item_" + item_index }>
                                                                                        <div className="topic_unit">{ item.floorplan_title }</div>
                                                                                        <div className="topic_number">{ item.floorplan_bathrooms }</div>
                                                                                        <div className="topic_number">{ item.floorplan_bedrooms }</div>
                                                                                        <div className="topic_number">{ item.event_count }</div>
                                                                                    </div>
                                                                                )
                                                                            })
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    );
}
